if (!customElements.get('theme-accordion')) {
  let accordionCounter = 0;

  class ThemeAccordion extends HTMLElement {
    constructor() {
      super();
      this.trigger = null;
      this.content = null;
      this.storageKey = this.dataset.storageKey || null;
      this.mediaQuery = null;
      this._dynamicTrigger = null;
      this._hiddenHeading = null;
      this._originalChildren = null;
      this.handleMediaChange = this.handleMediaChange.bind(this);
      this.handleTriggerClick = this.handleTriggerClick.bind(this);
      this.handleContentTransitionEnd = this.handleContentTransitionEnd.bind(this);
    }

    connectedCallback() {
      if (this.dataset.responsive) {
        const breakpoint = parseInt(this.dataset.breakpoint) || 750;
        this.mediaQuery = window.matchMedia(`(max-width: ${breakpoint - 1}px)`);
        this.mediaQuery.addEventListener('change', this.handleMediaChange);
        this.handleMediaChange(this.mediaQuery);
      } else {
        this.setup();
      }
    }

    disconnectedCallback() {
      this.mediaQuery?.removeEventListener('change', this.handleMediaChange);
      this.trigger?.removeEventListener('click', this.handleTriggerClick);
      this.content?.removeEventListener('transitionend', this.handleContentTransitionEnd);
    }

    handleMediaChange(event) {
      if (event.matches) {
        this.setup();
      } else {
        this.teardown();
      }
    }

    setup() {
      this.trigger = this.querySelector('[data-accordion-trigger]');
      this.content = this.querySelector('[data-accordion-content]');

      if (!this.trigger || !this.content) {
        this._buildDynamicStructure();
        return;
      }

      this.setupAccessibility();
      this.trigger.addEventListener('click', this.handleTriggerClick);
      this.content.addEventListener('transitionend', this.handleContentTransitionEnd);
      this.restoreState();
    }

    _buildDynamicStructure() {
      const headingEl = this.querySelector('.h1,.h2,.h3,.h4,.h5,h1,h2,h3,h4,h5,[class*="__heading"]');
      const triggerText = this.dataset.triggerText || headingEl?.textContent.trim() || '';
      const contentId = `theme-accordion-${++accordionCounter}`;

      const wrapper = document.createElement('div');
      wrapper.className = 'responsive-accordion__toggle';

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'responsive-accordion__trigger';
      button.setAttribute('aria-expanded', 'false');
      button.setAttribute('aria-controls', contentId);
      button.setAttribute('data-accordion-trigger', '');

      const label = document.createElement('span');
      label.className = 'responsive-accordion__label';
      label.textContent = triggerText;

      const icon = document.createElement('span');
      icon.className = 'responsive-accordion__icon';
      icon.setAttribute('aria-hidden', 'true');
      icon.innerHTML = '<svg class="icon icon-chevron-down" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none"><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M5 7.5l5 5 5-5"/></svg>';

      button.append(label, icon);

      const contentWrapper = document.createElement('div');
      contentWrapper.className = 'responsive-accordion__content';
      contentWrapper.id = contentId;
      contentWrapper.setAttribute('data-accordion-content', '');

      const contentInner = document.createElement('div');
      contentInner.className = 'accordion-toggle__content-inner';

      if (headingEl) {
        headingEl.style.display = 'none';
        this._hiddenHeading = headingEl;
      }

      this._originalChildren = [...this.children];
      this._originalChildren.forEach(child => {
        if (child !== headingEl) contentInner.appendChild(child);
      });

      contentWrapper.appendChild(contentInner);
      wrapper.append(button, contentWrapper);
      this.appendChild(wrapper);

      this._dynamicTrigger = wrapper;
      this.trigger = button;
      this.content = contentWrapper;

      this.trigger.addEventListener('click', this.handleTriggerClick);
      this.content.addEventListener('transitionend', this.handleContentTransitionEnd);
      this.restoreState();
    }

    teardown() {
      if (this._dynamicTrigger) {
        this.content?.removeEventListener('transitionend', this.handleContentTransitionEnd);
        const contentInner = this._dynamicTrigger.querySelector('.accordion-toggle__content-inner');
        if (contentInner) {
          [...contentInner.children].forEach(child => this.appendChild(child));
        }
        this._dynamicTrigger.remove();
        this._dynamicTrigger = null;

        if (this._hiddenHeading) {
          this._hiddenHeading.style.display = '';
          this._hiddenHeading = null;
        }
        this._originalChildren = null;
      } else if (this.trigger) {
        this.trigger.removeEventListener('click', this.handleTriggerClick);
        this.content?.removeEventListener('transitionend', this.handleContentTransitionEnd);
        this.trigger.removeAttribute('aria-expanded');
        this.trigger.removeAttribute('aria-controls');
        this.content?.setAttribute('data-expanded', 'true');
        if (this.content) this.content.style.overflow = '';
      }

      this.trigger = null;
      this.content = null;
    }

    setupAccessibility() {
      const contentId = this.content.id || `theme-accordion-${++accordionCounter}`;
      this.content.id = contentId;
      this.trigger.setAttribute('aria-controls', contentId);

      const isExpanded = this.hasAttribute('data-expanded');
      this.trigger.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');

      if (!isExpanded) {
        this.content.removeAttribute('data-expanded');
      } else {
        this.content.style.overflow = 'visible';
      }
    }

    handleContentTransitionEnd(event) {
      if (event.target !== this.content || event.propertyName !== 'grid-template-rows') return;
      this.content.style.overflow = this.content.hasAttribute('data-expanded') ? 'visible' : '';
    }

    handleTriggerClick() {
      this.toggle();
    }

    toggle() {
      this.trigger.getAttribute('aria-expanded') === 'true' ? this.close() : this.open();
    }

    open() {
      this.content.style.overflow = '';
      this.trigger.setAttribute('aria-expanded', 'true');
      this.content.setAttribute('data-expanded', 'true');
      this.saveState(true);
      this.publishEvent('open');

      const focusSelector = this.dataset.focusTarget || 'input, button, [tabindex="0"]';
      const focusTarget = this.content.querySelector(focusSelector);
      if (focusTarget) {
        requestAnimationFrame(() => focusTarget.focus());
      }
    }

    close() {
      this.content.style.overflow = '';
      this.trigger.setAttribute('aria-expanded', 'false');
      this.content.removeAttribute('data-expanded');
      this.saveState(false);
      this.publishEvent('close');
    }

    publishEvent(action) {
      if (typeof theme !== 'undefined' && theme.pubsub) {
        const eventName = this.dataset.event || 'accordion-toggle';
        theme.pubsub.publish(`${eventName}:${action}`, { element: this, isExpanded: action === 'open' });
      }

      this.dispatchEvent(new CustomEvent('accordion:toggle', {
        detail: { action, isExpanded: action === 'open' },
        bubbles: true,
      }));
    }

    saveState(isExpanded) {
      if (!this.storageKey) return;
      try {
        sessionStorage.setItem(this.storageKey, isExpanded ? 'true' : 'false');
      } catch (_) {}
    }

    restoreState() {
      if (!this.storageKey) return;
      try {
        const saved = sessionStorage.getItem(this.storageKey);
        if (saved === 'true') this.open();
        else if (saved === 'false') this.close();
      } catch (_) {}
    }
  }

  customElements.define('theme-accordion', ThemeAccordion);

  customElements.define('accordion-toggle', class extends ThemeAccordion {});
}

if (!customElements.get('theme-accordion-group')) {
  class ThemeAccordionGroup extends HTMLElement {
    constructor() {
      super();
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleAccordionToggle = this.handleAccordionToggle.bind(this);
    }

    connectedCallback() {
      this.addEventListener('keydown', this.handleKeydown);
      if (this.hasAttribute('data-single-expanded')) {
        this.addEventListener('accordion:toggle', this.handleAccordionToggle);
      }
    }

    disconnectedCallback() {
      this.removeEventListener('keydown', this.handleKeydown);
      this.removeEventListener('accordion:toggle', this.handleAccordionToggle);
    }

    get accordions() {
      return [...this.querySelectorAll(':scope > theme-accordion, :scope > accordion-toggle')];
    }

    get triggers() {
      return this.accordions.map(a => a.querySelector('[data-accordion-trigger]')).filter(Boolean);
    }

    handleAccordionToggle(event) {
      if (event.detail.action !== 'open') return;
      const source = event.target;
      this.accordions.forEach(accordion => {
        if (accordion !== source && accordion.trigger?.getAttribute('aria-expanded') === 'true') {
          accordion.close();
        }
      });
    }

    handleKeydown(event) {
      const triggers = this.triggers;
      const index = triggers.indexOf(document.activeElement);
      if (index === -1) return;

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        triggers[(index + 1) % triggers.length].focus();
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        triggers[(index - 1 + triggers.length) % triggers.length].focus();
      } else if (event.key === 'Home') {
        event.preventDefault();
        triggers[0].focus();
      } else if (event.key === 'End') {
        event.preventDefault();
        triggers[triggers.length - 1].focus();
      }
    }
  }

  customElements.define('theme-accordion-group', ThemeAccordionGroup);
}
